import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";

import { atividadeParaTrilha } from "./atividadeParaTrilha";
import {
  carregarPreferenciaTelaCheia,
  carregarProgressoLocal,
  derivarSorteioComuns,
  derivarSorteioRaras,
  limparProgressoLocal,
  pediuReset,
  salvarPreferenciaTelaCheia,
  salvarProgressoLocal
} from "./progressoDoAluno";
import { corpoDeSincronia, juntarProgresso } from "./sincronia";
import {
  chanceDeRaraForcada,
  quantasComunsForcadas,
  regiaoForcada
} from "./visualizacao";
import { AVATARES, AVATAR_PADRAO, getAvatar, getRegiao } from "../conteudo";
import { cardsDoAlbum } from "../nucleo/album";
import CerimoniaCard from "../nucleo/CerimoniaCard";
import Encerramento from "../nucleo/Encerramento";
import ModoTelaCheia from "../nucleo/ModoTelaCheia";
import Onboarding from "../nucleo/Onboarding";
import { CHANCE_DE_CARTA_RARA, cenaDaFase } from "../nucleo/recompensaDaFase";
import Trilha from "../nucleo/Trilha";

import "./modalDoItem.css";

// Atividade encerrada: o backend não registra mais conclusão de item
// (createItemExecutionOnClose aborta quando `late`), então as fases não avançam
// mais. Sem esse aviso o aluno fica clicando num nó que nunca completa.
const AVISO_ENCERRADA =
  "O prazo desta atividade terminou. Você ainda pode abrir e rever os conteúdos, mas as fases não avançam mais.";

const MENSAGEM_SEM_ITENS = "Esta atividade ainda não tem itens para percorrer.";

/**
 * Liga a trilha lúdica a uma ATIVIDADE do AVA.
 *
 * É a única peça que conhece os dois lados: recebe o payload de
 * `singleViewByExecution` e devolve, no clique, o item original que o
 * `openItem` da página consome.
 *
 * Para plugar a trilha em outro contexto (passos de trilha de aprendizagem,
 * olimpíadas, ...), escreve-se um irmão deste arquivo com o seu adaptador —
 * `nucleo/` não muda. Ver ../README.md.
 *
 * @param {object}   activity      atividade como vem da API
 * @param {Array}    itens         itens da atividade (já enriquecidos pela página)
 * @param {Function} onAbrirItem   recebe o item original
 * @param {number}   userId        aluno logado, para a chave do progresso local
 * @param {Function} [onSincronizar]  recebe o corpo pronto do POST que salva a
 *   trilha no servidor. Opcional: sem ele a trilha funciona só com
 *   `localStorage`, que é como ela viveu até 01/08/2026. Quem faz o HTTP é o
 *   host — nenhum import sai deste módulo (ver ../README.md).
 */
function TrilhaDeAtividade({
  activity,
  itens,
  onAbrirItem,
  userId,
  location,
  onSincronizar
}) {
  const trilha = useMemo(
    () => atividadeParaTrilha({ ...activity, items: itens }),
    [activity, itens]
  );

  const activityId = activity && activity.id;

  // QUEM DECIDE A REGIÃO É O BACKEND, pelo `trailRegion` de
  // `singleViewByExecution` — gravado quando o professor criou a trilha e imutável
  // desde então (`LudicTrailSetting::store` cria e nunca atualiza). Uma linha, e é
  // a única do módulo que muda para a trilha ser multi-região: `nucleo/` já recebia
  // a região por prop e as funções puras já a recebiam por parâmetro.
  //
  // `getRegiao` cai na padrão para id desconhecido, e o fallback não é cortesia: a
  // coluna aceita qualquer string de 32 caracteres, e sem ele a cerimônia de card e
  // o encerramento quebrariam com o aluno no meio da atividade.
  //
  // Referência ESTÁVEL entre renderizações (é o mesmo objeto do catálogo), o que
  // permite usá-la como dependência de efeito sem provocar reexecução.
  //
  // `?regiao=norte` na URL tem a palavra final, e só fora de produção — é o irmão
  // do `?view=trilha`, e existe para as ferramentas de medição poderem abrir uma
  // região específica sem depender de haver, em homologação, uma trilha criada
  // naquela região. Ver ./visualizacao.js.
  const regiao = getRegiao(
    regiaoForcada(location) || (activity && activity.trailRegion)
  );

  /* O MODAL DO ITEM GANHA CANTOS ARREDONDADOS enquanto a trilha está na tela, e o
     que sai daqui para isso acontecer é UM ATRIBUTO. Ver `./modalDoItem.css`.

     Quem abre o conteúdo quando o aluno toca num nó é o AVA, com o
     `components/Interface` — um `CModal` do CoreUI compartilhado com a trilha de
     aprendizagem e as Olimpíadas. Tematizá-lo pelo componente mudaria o AVA inteiro;
     passar uma prop atravessaria três arquivos do host com um assunto que é da
     trilha. O atributo deixa o componente intacto e some quando a trilha desmonta.

     No BODY porque o CoreUI 3 renderiza o modal INLINE (conferido em
     `src/modal/CModal.js`), na árvore de quem o declara — e quem o declara é a página
     `Do`, IRMÃ do bloco da trilha. Não há ancestral comum mais perto.

     SÓ O ATRIBUTO, e nenhuma cor: houve aqui uma versão que também escrevia a paleta
     da região no body, para o modal ganhar cabeçalho colorido e borda. O Kilson viu
     na tela e recusou em 06/08/2026 — ficou só o arredondamento, que não precisa de
     cor nenhuma. Escrever variáveis que ninguém lê seria peso morto no elemento mais
     compartilhado da página.

     Este é o ÚNICO ponto em que o módulo escreve fora de si, e ele está em `ava/`,
     que por contrato é a camada que conhece o host. O `nucleo/` continua sem saber
     que existe AVA. */
  useEffect(() => {
    if (typeof document === "undefined" || !regiao) return undefined;

    const { body } = document;
    body.setAttribute("data-tl-regiao", regiao.id);

    // A limpeza não é detalhe: sem ela o gancho fica no body depois de o aluno sair
    // da trilha, e o próximo modal do AVA — em qualquer tela — herda o tratamento.
    return () => body.removeAttribute("data-tl-regiao");
  }, [regiao]);

  // Sorteio dos cards comuns e avatar escolhido. Não é progresso — as fases
  // concluídas vêm do servidor; isto é estado de experiência (decisão I-7).
  const [local, setLocal] = useState(() => {
    // `?trilha=reset` na URL devolve a trilha ao estado de primeira entrada.
    // Roda no inicializador (uma vez por montagem), então o refetch da atividade
    // não apaga a escolha que o aluno acabou de fazer.
    if (pediuReset(location)) limparProgressoLocal(userId, activityId);

    const doNavegador = carregarProgressoLocal(
      userId,
      activityId,
      trilha.totalFases,
      regiao.cardsComuns
    );

    // O servidor manda quando existe — é ele que sobrevive à troca de
    // dispositivo. `trailExperience` vem null na primeira entrada, e aí o
    // navegador (ou a derivação) responde. Ver ./sincronia.js.
    //
    // No inicializador, e não num efeito: se o avatar chegasse depois da
    // primeira renderização, o aluno veria o onboarding piscar antes de o
    // tabuleiro aparecer.
    return juntarProgresso(activity && activity.trailExperience, doNavegador);
  });

  // O QUE ESTÁ NA CERIMÔNIA: `{ card }` (o aluno ganhou a carta) ou `{ arco }` (a
  // cena do fim de leva, com o mascote contando a sua parte da história e nenhum
  // prêmio). Era `cardNaTela` até 24/08/2026, quando a carta rara passou a ser
  // sorteada e o arco deixou de depender dela.
  const [cenaNaTela, setCenaNaTela] = useState(null);

  // A chance, resolvida FORA da memo abaixo e guardada num número. É de propósito:
  // `location` é objeto, e depender dele deixaria a referência do sorteio à mercê da
  // identidade que o router devolve a cada renderização — um número não tem esse
  // problema. `??` e não `||` porque `0` (`?raras=nenhuma`) é uma chance válida, e
  // com `||` o override mais importante dos dois seria o único a não funcionar.
  const chanceDeRara = chanceDeRaraForcada(location) ?? CHANCE_DE_CARTA_RARA;

  // Quantas comuns forçar, ou `null` para a decisão de produto. Mesmo cuidado da
  // linha acima — resolvido fora da memo e guardado num número, porque `location` é
  // objeto e a identidade que o router devolve muda a cada renderização.
  const quantasComuns = quantasComunsForcadas(location);

  // OS DOIS SORTEIOS, num objeto só. `comuns` é guardado (depende do banco de cards
  // da região); `raras` é derivado a cada carga de (aluno, atividade) e não mora em
  // lugar nenhum — ver `derivarSorteioRaras`.
  //
  // Em `useMemo` porque `derivarSorteioRaras` reconstrói o gerador a cada chamada:
  // o resultado é o mesmo, mas recalcular a cada renderização daria uma referência
  // nova e reexecutaria os efeitos que dependem dele.
  //
  // Com `?cartas=<n>` os comuns vêm da derivação SEM PASSAR PELO DISCO, e não do
  // guardado: o sorteio dos comuns é persistido, então o valor forçado seria gravado
  // na primeira carga e lido de volta na segunda — o override pareceria não
  // funcionar. Ver `quantasComunsForcadas` em ./visualizacao.js.
  const sorteio = useMemo(
    () => ({
      comuns:
        quantasComuns === null
          ? local.sorteioComuns
          : derivarSorteioComuns(
              userId,
              activityId,
              trilha.totalFases,
              regiao.cardsComuns,
              quantasComuns
            ),
      raras: derivarSorteioRaras(
        userId,
        activityId,
        trilha.totalFases,
        chanceDeRara
      )
    }),
    [
      local.sorteioComuns,
      userId,
      activityId,
      trilha.totalFases,
      chanceDeRara,
      quantasComuns,
      regiao.cardsComuns
    ]
  );

  // Tela cheia é preferência de DISPOSITIVO, não desta atividade nem deste
  // aluno, e por isso não mora no objeto do progresso local (ver
  // progressoDoAluno.js). Persistir importa mais do que parece: abrir um item não
  // desmonta a trilha — os itens abrem em modal da página `Do` —, mas recarregar
  // a página desmonta, e sem persistência o aluno seria jogado de volta para o
  // fluxo normal a cada F5, tendo de religar o modo toda vez.
  const [telaCheia, setTelaCheia] = useState(carregarPreferenciaTelaCheia);

  const alternarTelaCheia = useCallback(valor => {
    setTelaCheia(salvarPreferenciaTelaCheia(valor));
  }, []);

  // Sobe para o servidor o que mudou: explorador, sorteio, telas vistas e a
  // lista completa das cartas já conquistadas.
  //
  // Dispara por MUDANÇA DE CONTEÚDO, não por evento: a assinatura do corpo é
  // comparada com a do último envio, e igual não vai. Foi o jeito de cobrir de
  // uma vez as três coisas que precisam subir — escolher o explorador, ver o
  // encerramento e concluir uma fase premiada —, sem espalhar chamadas por três
  // lugares e sem reenviar a cada refetch da atividade (a página `Do` refetcha a
  // cada modal fechado).
  //
  // Não trata erro nem espera resposta: o `localStorage` já foi escrito e a
  // reconciliação é auto-corretiva, então um POST perdido se conserta na próxima
  // carga. A regra do módulo é que a trilha jamais impeça o aluno de fazer a
  // atividade — e isso vale para a rede também.
  const ultimaSincronia = useRef(null);

  useEffect(() => {
    if (!onSincronizar || !activityId || trilha.totalFases === 0) return;

    // Com o override de QA ligado NADA sobe: o corpo levaria `drawn_cards` com o
    // layout forçado, e a coluna do servidor tem precedência sobre tudo na volta
    // (ver ./sincronia.js). O estado de QA sobreviveria à limpeza do navegador e à
    // troca de dispositivo — exatamente o que `?trilha=reset` existe para evitar.
    if (quantasComuns !== null) return;

    const corpo = corpoDeSincronia({
      activityId,
      local,
      trilha,
      regiao,
      sorteio
    });

    const assinatura = JSON.stringify(corpo);
    if (assinatura === ultimaSincronia.current) return;

    ultimaSincronia.current = assinatura;
    onSincronizar(corpo);
  }, [
    onSincronizar,
    activityId,
    local,
    trilha,
    regiao,
    sorteio,
    quantasComuns
  ]);

  // Snapshot das fases já concluídas na renderização anterior. É comparando
  // com o novo que se sabe QUAL fase acabou de ser concluída — a conclusão é
  // confirmada pelo servidor no refetch, não no clique, então não há como
  // saber disso no momento em que o aluno clica.
  const concluidasAntes = useRef(null);

  useEffect(() => {
    const agora = new Set(
      trilha.fases.filter(fase => fase.concluida).map(fase => fase.numero)
    );

    // Primeira renderização: só registra o ponto de partida. Sem isto, um aluno
    // que reabre uma atividade já iniciada receberia de novo os cards de todas
    // as fases que concluiu em sessões anteriores.
    if (concluidasAntes.current === null) {
      concluidasAntes.current = agora;
      return;
    }

    const novas = [...agora].filter(
      numero => !concluidasAntes.current.has(numero)
    );
    concluidasAntes.current = agora;

    if (novas.length === 0) return;

    // Se mais de uma fase concluiu entre dois refetches (raro, mas possível se
    // o aluno abrir itens em outra aba), premia a menor — a ordem da narrativa
    // do arco importa.
    //
    // `cenaDaFase` e não `recompensaDaFase`: numa fase rara sem carta sorteada a
    // segunda devolve `null`, e a cerimônia não abriria — o aluno perderia a parte
    // do mascote no arco, que é justamente o que a mudança de 24/08 preserva.
    const cena = novas
      .sort((a, b) => a - b)
      .map(numero => cenaDaFase(numero, trilha.totalFases, regiao, sorteio))
      .find(Boolean);

    if (!cena) return;

    setCenaNaTela(cena);
  }, [trilha, sorteio, regiao]);

  const avatar = getAvatar(local.avatarId) || AVATAR_PADRAO;

  const trilhaCompleta =
    trilha.totalFases > 0 && trilha.fases.every(fase => fase.concluida);

  // O encerramento espera a cerimônia sair da tela. A última fase é ao mesmo
  // tempo a última E uma fase premiada (a regra põe card raro no fim de cada
  // leva), então concluir a trilha dispara as duas coisas na mesma renderização
  // — no protótipo isso era um bug: a navegação para o encerramento só existia
  // no ramo "sem card", e a última fase nunca chegava lá.
  //
  // Aqui não há flag para isso: `cenaNaTela` já é o estado que diz "tem
  // cerimônia aberta", e derivar dele torna a ordem impossível de errar. Quando
  // o aluno fecha a cerimônia, o encerramento aparece.
  const mostrarEncerramento =
    trilhaCompleta && !cenaNaTela && !local.encerramentoVisto;

  const cardsConquistados = useMemo(
    () =>
      mostrarEncerramento
        ? cardsDoAlbum(trilha.fases, trilha.totalFases, regiao, sorteio)
        : [],
    [mostrarEncerramento, trilha, sorteio, regiao]
  );

  // Primeira entrada = ainda não escolheu explorador. É o avatar que marca isso,
  // não uma flag separada: se um dia o progresso local for perdido (outro
  // navegador, cache limpo), o aluno reescolhe — e reescolher é inofensivo,
  // enquanto ficar sem avatar não é.
  const precisaOnboarding = trilha.totalFases > 0 && !local.avatarId;

  function concluirOnboarding(avatarEscolhido) {
    setLocal(atual =>
      salvarProgressoLocal(userId, activityId, {
        ...atual,
        avatarId: avatarEscolhido,
        introVisto: true
      })
    );
  }

  function fecharEncerramento() {
    setLocal(atual =>
      salvarProgressoLocal(userId, activityId, {
        ...atual,
        encerramentoVisto: true
      })
    );
  }

  // Qual das três telas está no ar. Calculado em variável em vez de `return`
  // antecipado (como era até 29/07) porque o modo TELA CHEIA envolve as três: a
  // moldura tem de ser a MESMA em todas, senão alternar de tela desmontaria e
  // remontaria o overlay, e o modo se apagaria na passagem onboarding → trilha.
  //
  // O onboarding vem ANTES do encerramento de propósito. Um aluno pode chegar
  // aqui com a trilha já completa e sem avatar — concluiu tudo na lista clássica,
  // ou noutro navegador. Celebrar primeiro e só depois perguntar quem ele é
  // inverteria a apresentação; e o resto da tela conta com um avatar escolhido.
  let conteudo;

  if (precisaOnboarding) {
    conteudo = (
      <Onboarding
        regiao={regiao}
        avatares={AVATARES}
        avatarId={local.avatarId}
        // O mapa é a boas-vindas da trilha; quem já passou por ele e só está
        // reescolhendo o explorador não precisa vê-lo de novo.
        pularMapa={local.introVisto}
        onConcluir={concluirOnboarding}
      />
    );
  } else if (mostrarEncerramento) {
    conteudo = (
      <Encerramento
        regiao={regiao}
        cards={cardsConquistados}
        onFechar={fecharEncerramento}
      />
    );
  } else {
    conteudo = (
      <Trilha
        trilha={trilha}
        avatar={avatar.asset}
        aviso={trilha.encerrada ? AVISO_ENCERRADA : undefined}
        mensagemVazia={MENSAGEM_SEM_ITENS}
        regiao={regiao}
        sorteio={sorteio}
        // Enquanto a carta está na tela, a trilha não troca de leva. A fase que
        // abre a leva 2 é justamente a fase rara (a regra põe card raro no fim de
        // cada leva), então as duas coisas chegam no MESMO refetch: sem isto a
        // cortina de neblina rodaria atrás do scrim da cerimônia e o aluno veria
        // no máximo um cinza passando. Esperando a carta sair, ele vê a transição
        // que o protótipo entrega — a tela clareia e a continuação do tabuleiro
        // aparece — e de graça o tabuleiro para de saltar de altura por trás da
        // carta enquanto ele lê a curiosidade.
        adiarPassagem={Boolean(cenaNaTela)}
        onAbrirFase={fase => onAbrirItem(fase.referencia)}
      />
    );
  }

  // Atividade sem itens não tem o que ver em tela cheia — seria uma frase só no
  // meio de uma página de areia, e com a preferência ligada o aluno cairia nela
  // direto. Sem trilha, sem modo e sem botão. Também não há cerimônia possível
  // aqui: recompensa depende de fase concluída.
  if (trilha.totalFases === 0) return conteudo;

  return (
    <ModoTelaCheia
      ativo={telaCheia}
      onAlternar={alternarTelaCheia}
      regiao={regiao}
    >
      {conteudo}

      {/* Fora do `conteudo` porque a cerimônia se soma à tela que está no ar em
          vez de substituí-la, e DENTRO da moldura porque ela é um portal para o
          body: em tela cheia sobe acima do overlay pelo z-index (1060 contra
          1035), não pela posição na árvore. */}
      {cenaNaTela && (
        <CerimoniaCard
          card={cenaNaTela.card}
          arco={cenaNaTela.arco}
          mascote={regiao.mascote}
          // Fechar a cerimônia não grava nada: o álbum do encerramento é DERIVADO
          // das fases concluídas (ver nucleo/album.js), não de uma lista de
          // cards coletados no navegador. Vale igual para a cena do arco, que não
          // tem carta nenhuma para gravar.
          onFechar={() => setCenaNaTela(null)}
        />
      )}
    </ModoTelaCheia>
  );
}

export default TrilhaDeAtividade;
